<template>
    <div>
        <div class="horizontal">
            <div :id="'scichart-root-herd-weight-'+sid" style="width: 100%; margin-right: 10px;"></div>
        </div>
    </div>
</template>

<script>
import moment from 'moment'

import { SciChartSurface } from "scichart/Charting/Visuals/SciChartSurface";
import { NumericAxis } from "scichart/Charting/Visuals/Axis/NumericAxis";
import { EAxisAlignment } from "scichart/types/AxisAlignment";
import { FastLineRenderableSeries } from "scichart/Charting/Visuals/RenderableSeries/FastLineRenderableSeries";
import { XyDataSeries } from "scichart/Charting/Model/XyDataSeries";
import { RolloverModifier } from "scichart/Charting/ChartModifiers/RolloverModifier";
import { EllipsePointMarker } from "scichart/Charting/Visuals/PointMarkers/EllipsePointMarker";
import { chartThemeMixin } from "@/utils/chartTheme";
import { ELineDrawMode } from "scichart/Charting/Drawing/WebGlRenderContext2D"
import { NumberRange } from 'scichart/Core/NumberRange';
import { XyLinearTrendFilter } from 'scichart/Charting/Model/Filters/XyLinearTrendFilter';

export default {
    mixins: [chartThemeMixin],
    props: {
        chartData: {
            required: true,
        },
        sid: {
            default: 0,
            required: false,
        },
        unit: {
            dafault: 'kg',
            required: false
        }
    },
    data() {
        return {
            sciChartSurface: null,
            wasmContext: null,
            xAxis: null,
            yAxis: null
        }
    },
    watch: {
        chartData(newVal, oldVal) {
            if(oldVal == null) {
                this.initSciChart(newVal);
            } else {
                this.reload(newVal);
            }
        }
    },
    methods: {
        async initSciChart(data) {
            const {sciChartSurface, wasmContext} = await SciChartSurface.create('scichart-root-herd-weight-'+this.sid);

            this.sciChartSurface = sciChartSurface;
            this.wasmContext = wasmContext;

            sciChartSurface.applyTheme(this.chartTheme())

            this.xAxis = new NumericAxis(this.wasmContext, { axisAlignment: EAxisAlignment.Top});
            this.yAxis = new NumericAxis(this.wasmContext, { axisAlignment: EAxisAlignment.Left});

            this.yAxis.growBy = new NumberRange(0.1, 0.1);
            this.xAxis.growBy = new NumberRange(0.01, 0.01);

            sciChartSurface.xAxes.add(this.xAxis)
            sciChartSurface.yAxes.add(this.yAxis)

            this.xAxis.labelProvider.formatLabel = (unixTimestamp) => {
                return moment(unixTimestamp).format("YYYY-MM-DD")
            };

            this.populateData(data);

            const rolloverModifier = new RolloverModifier();
            rolloverModifier.showRolloverLine = false;
            this.sciChartSurface.chartModifiers.add(rolloverModifier);
        },
        async reload(data) {
            // if(this.wasmContext == null) {
            //     this.initSciChart();
            //     return;
            // }

            if(this.sciChartSurface.renderableSeries != null){
                this.sciChartSurface.renderableSeries.items = [];
            }

            this.populateData(data);
        },
        populateData(data) {
            var xValues = data.labels;
            var yValues = data.values;
            
            var d = [];

            const lineSeries = new FastLineRenderableSeries(this.wasmContext, {
                stroke: "#0095B2",
                drawNaNAs: ELineDrawMode.PolyLine,
                strokeThickness: 2,
                pointMarker: new EllipsePointMarker(this.wasmContext, {
                    width: 6,
                    height: 6,
                    strokeThickness: 2,
                    fill: "#0095B230",
                    stroke: "#0095B2"
                })
            });

            if(!(xValues == undefined || xValues == null || xValues.length == 0)) {
                for(let i = 0; i < xValues.length; i++){
                    d[i] = new Date(moment(xValues[i])).getTime()
                }

                if(this.unit == '') {
                    yValues = yValues.map(x => parseInt(x))
                } else {
                    yValues = yValues.map(x => parseFloat(parseFloat(x).toFixed(2)))
                }

                lineSeries.dataSeries = new XyDataSeries(this.wasmContext, {
                    xValues: d,
                    yValues: yValues
                });

                lineSeries.strokeThickness = 3;
                lineSeries.rolloverModifierProps.tooltipLabelX = this.labelX ? this.labelX : "X";
                lineSeries.rolloverModifierProps.tooltipLabelY = this.labelY ? this.labelY : "Y";
                lineSeries.rolloverModifierProps.tooltipTemplate = this.tooltipTemplateMethod;

                // TREND LINE - z jednego pomiaru regresja liczy 0/0 i daje NaN, ktore psuje os Y.
                if(d.length >= 2) {
                    const linearTrendFilter = new XyLinearTrendFilter(
                        new XyDataSeries(this.wasmContext, {
                            xValues: d,
                            yValues: yValues
                        }),
                        { dataSeriesName: this.$t('trendLine') }
                    );
                    const filteredLine = new FastLineRenderableSeries(this.wasmContext, {
                        dataSeries: linearTrendFilter,
                        stroke: "#ff0000",
                        strokeThickness: 2,
                        rolloverModifier: null
                    });
                    filteredLine.rolloverModifierProps.tooltipTemplate = this.trendTemplateMethod;

                    this.sciChartSurface.renderableSeries.add(filteredLine)
                }
                // END OF TREND LINE
            } else {
                lineSeries.dataSeries = new XyDataSeries(this.wasmContext, {
                    xValues: [],
                    yValues: []
                });
            }

            this.sciChartSurface.renderableSeries.add(lineSeries);
            this.sciChartSurface.zoomExtents();
        },
        tooltipTemplateMethod(id, seriesInfo, rolloverTooltip) {
            const colors = this.tooltipColors();
            // Rozmiar zgłaszany SciChartowi = prostokąt z obrysem. Dawne 200x120
            // odsuwało dymek od punktu w lewo i w górę, poza wąski wykres.
            const width = 152;
            const height = 42;
            rolloverTooltip.updateSize(width, height);

            return `<svg width="${width}" height="${height}">
                <rect x="1" y="1" height="40" width="150" rx="5" style="stroke-width:1; stroke:${colors.border}" fill="${colors.background}"/>
                <svg>
                    <text font-size="13" font-family="Verdana" fill="${colors.text}">
                        <tspan x="11" dy="1.2em">${moment(Math.floor(seriesInfo.formattedXValue)).format("DD-MM-YYYY")}</tspan>
                        <tspan x="11" dy="1.2em">${this.unit == '' ? parseInt(seriesInfo.formattedYValue) : parseFloat(seriesInfo.formattedYValue).toFixed(2)} ${this.unit}</tspan>
                    </text>
                    <defs>
                        <filter id="f1" x="0" y="0" width="200%" height="200%">
                        <feOffset result="offOut" in="SourceGraphic" dx="20" dy="20" />
                        <feBlend in="SourceGraphic" in2="offOut" mode="normal" />
                        </filter>
                    </defs>
                </svg>
                </svg>`;
        },
        trendTemplateMethod(id, seriesInfo, rolloverTooltip) {
            const { tooltipColor } = rolloverTooltip.tooltipProps;
            const colors = this.tooltipColors();
            const width = 152;
            const height = 32;
            rolloverTooltip.updateSize(width, height);

            return `<svg width="${width}" height="${height}">
                <rect x="1" y="1" height="30" width="150" rx="5" style="stroke-width:2; stroke:${tooltipColor}" fill="${colors.background}"/>
                <svg>
                    <text font-size="13" font-family="Verdana" fill="${colors.text}">
                        <tspan x="11" dy="1.2em">${this.unit == '' ? parseInt(seriesInfo.formattedYValue) : seriesInfo.formattedYValue} ${this.unit}</tspan>
                    </text>
                    <defs>
                    <filter id="f1" x="0" y="0" width="200%" height="200%">
                        <feOffset result="offOut" in="SourceGraphic" dx="20" dy="20" />
                        <feBlend in="SourceGraphic" in2="offOut" mode="normal" />
                    </filter>
                    </defs>
                </svg>
            </svg>`;
        },
    },
    mounted: async function() {
        this.initSciChart(this.chartData);
    },
    beforeDestroy: async function() {
        if(this.sciChartSurface || this.wasmContext){
            this.sciChartSurface = null
            this.wasmContext = null
        }
        this.sciChartSurface = null
        this.wasmContext = null
    }
}
</script>

<style scoped>

</style>


// WEBPACK FOOTER //
// src/components/Charts/SciChartAnimalData.vue